﻿import React, { useCallback } from 'react';

import { ChartStackedProgressBar } from 'pages/reports/components/ChartStackedProgressBar/ChartStackProgressBar';
import { ChartProgressBar } from 'pages/reports/components/ChartProgressBar/ChartProgressBar';
import { ChartActionItem } from 'pages/reports/components/ChartActionItem/ChartActionItem';
import { ChartGeneric } from 'pages/reports/components/ChartGeneric/ChartGeneric';
import { ChartInfoBox } from 'pages/reports/components/ChartInfoBox/ChartInfoBox';
import { TableGeneric } from 'pages/reports/components/TableGeneric/TableGeneric';
import { GenericHTML } from 'pages/reports/components/GenericHTML/GenericHTML';
import { SkillTree } from 'pages/reports/components/SkillTree/SkillTree';
import { SubReport } from 'pages/reports/components/SubReport/SubReport';
import { GeneralGauge } from 'components/GeneralGaugeChart/GeneralGauge';
import { Report, ReportComponent, ReportSection } from 'apis/ReportApi';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixTabs, GMetrixTabsTab } from 'components/GMetrixTabs';
import { Col, Container, Row } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { useSuperState } from 'hooks';

import 'components/ComponentWrapper/component-wrapper.scss';

interface ComponentWrapperProps {
    hasResults: boolean;
    isSubReport?: boolean;
    loading: boolean;
    reportSections: ReportSection[]
}

export function ComponentWrapper({ hasResults, isSubReport = false, loading, reportSections }: ComponentWrapperProps) {

    const [state, setState] = useSuperState({
        currentActionData: undefined as ReportComponent | undefined,
        currentDialog: undefined as JSX.Element | undefined,
        currentSubReport: undefined as Report | undefined,
        selected: 0,
        showActionModal: false
    });
    const { currentActionData, currentDialog, currentSubReport, selected, showActionModal } = state;

    let currentTabGroup: ReportSection[] = [];
    let tempGroup: ReportSection[] = [];

    const handleSelect = (index: number) => setState({ selected: index });

    // Skill tree, info-box (e.g. products), and action item sections render on white instead of the default grey section background.
    const WHITE_SECTION_TYPES = ['ListOfInfoBox', 'ListOfActionItem'];
    const sectionClassName = (section: ReportSection) => `component-wrapper mb-2 border rounded${section.Components.some(c => c.ComponentName === 'Skill Tree' || WHITE_SECTION_TYPES.includes(c.Type ?? '')) ? ' component-wrapper--white' : ''}`;

    const handleSetCurrentActionData = useCallback((data: ReportComponent | undefined) => setState({ currentActionData: data }), []);
    const handleSetCurrentSubReport = useCallback((report: Report | undefined) => setState({ currentSubReport: report }), []);
    const handleSetShowActionModal = useCallback((show: boolean) => setState({ showActionModal: show }), []);
    const handleSetCurrentDialog = useCallback((dialog: JSX.Element | undefined) => setState({ currentDialog: dialog }), []);

    const renderChart = (component: ReportComponent, loading: boolean, index: number) => {

        switch (component.Type) {

            case 'ListOfActionItem':
                return (
                    <ChartActionItem
                        component={component}
                        index={index}
                        isSubReport={isSubReport}
                        key={index}
                        loading={loading}
                        setCurrentActionData={handleSetCurrentActionData}
                        setCurrentSubReport={handleSetCurrentSubReport}
                        setShowActionModal={handleSetShowActionModal}
                    />
                );
            case 'ListOfInfoBox':
                return (
                    <ChartInfoBox
                        component={component}
                        index={index}
                        isSubReport={isSubReport}
                        key={index}
                        loading={loading}
                    />
                );
            case 'ListOfProgressBar':
                return (
                    <ChartProgressBar
                        component={component}
                        generateFakeProgressBars={generateFakeProgressBars}
                        index={index}
                        isSubReport={isSubReport}
                        key={index}
                        loading={loading}
                    />
                );
            case 'ListOfStackedProgressBar':
                return (
                    <ChartStackedProgressBar
                        component={component}
                        generateFakeProgressBars={generateFakeProgressBars}
                        index={index}
                        isSubReport={isSubReport}
                        key={index}
                        loading={loading}
                    />
                );

            default:
                return (
                    <ChartGeneric
                        chartLegendVisible
                        centerTitle={component.CenterTitle}
                        component={component}
                        index={index}
                        isSubReport={isSubReport}
                        key={index}
                        loading={loading}
                    />
                );

        }

    };

    const renderComponents = (component: ReportComponent, loading: boolean, index: number) => {

        switch (component.ComponentName) {

            case 'Gauge':
                return (
                    <GeneralGauge
                        component={component}
                        index={index}
                        isSubReport={isSubReport}
                        key={index}
                        loading={loading}
                    />
                );
            case 'Chart':
                return renderChart(component, loading, index);
            case 'SubReport':
                return (
                    <SubReport
                        component={component}
                        currentSubReport={currentSubReport}
                        index={index}
                        key={index}
                        loading={loading}
                        renderComponents={renderComponents}
                    />
                );
            case 'HTMLContainer':
                return (
                    <GenericHTML
                        component={component}
                        currentDialog={currentDialog}
                        index={index}
                        isSubReport={isSubReport}
                        key={index}
                        loading={loading}
                        setCurrentDialog={handleSetCurrentDialog}
                    />
                );
            case 'Skill Tree':
                return (
                    <SkillTree
                        component={component}
                        generateFakeProgressBars={generateFakeProgressBars}
                        index={index}
                        isSubReport={isSubReport}
                        key={index}
                        loading={loading}
                        treeData={component.Results?.Data ?? []}
                    />
                );
            default:
                return (
                    <TableGeneric
                        component={component}
                        index={index}
                        isSubReport={isSubReport}
                        key={index}
                        loading={loading}
                        setCurrentSubReport={handleSetCurrentSubReport}
                    />
                );

        }

    };

    const generateFakeProgressBars = (countOfProgressBars: number) => {

        const fakeArrayCount = [];
        for (let i = 0; i < countOfProgressBars; i++) {

            fakeArrayCount.push(i);

        }
        return fakeArrayCount.map(i => (
            <Row className="mb-4 d-flex flex-column align-items-center" key={i}>
                <Col className="skeleton gray progress-bar-fake p-2 mb-1 w-75"></Col>
                <Col className="skeleton gray progress-bar-fake p-2 w-75"></Col>
            </Row>
        ));

    };

    return (
        <>
            <GMetrixDialog
                actionButtonDisabled={false}
                onCancel={() => setState({ showActionModal: false })}
                show={showActionModal}
            >
                {currentActionData && (
                    <ChartActionItem
                        component={currentActionData}
                        index={1}
                        loading={loading}
                        onActionButtonClick={handleSetShowActionModal}
                        setCurrentActionData={handleSetCurrentActionData}
                        setCurrentSubReport={handleSetCurrentSubReport}
                        setShowActionModal={handleSetShowActionModal}
                        showActionButton={false}
                    />
                )}
            </GMetrixDialog>

            {currentDialog}
            {!loading ? (

                // report has results and is done loading
                hasResults ? (
                    reportSections.map((section, reportSectionIndex) => {

                        if (tempGroup.length > 0) {

                            currentTabGroup = [];

                        }
                        if (section.TabGroup) {

                            currentTabGroup.push(section);
                            if (reportSectionIndex === reportSections.length - 1) {

                                tempGroup = { ...currentTabGroup };
                                return (
                                    <GMetrixTabs
                                        className="mb-2"
                                        key={`${reportSectionIndex}-extra`}
                                        onSelect={handleSelect}
                                        selected={selected}
                                    >
                                        {currentTabGroup.map((tab, index) => (
                                            <GMetrixTabsTab key={index} title={tab.Title}>
                                                <Container fluid className={sectionClassName(tab)}>
                                                    <Row>
                                                        {tab.Components.map((component, componentSectionIndex) => renderComponents(
                                                            component,
                                                            loading,
                                                            componentSectionIndex
                                                        ))}
                                                    </Row>
                                                </Container>
                                            </GMetrixTabsTab>
                                        ))}
                                    </GMetrixTabs>
                                );

                            }
                            return null;

                        }
                        if (currentTabGroup.length > 0) {

                            tempGroup = { ...currentTabGroup };

                        }
                        return (
                            <React.Fragment key={reportSectionIndex}>
                                {currentTabGroup.length > 0 && (
                                    <GMetrixTabs
                                        className="mb-2"
                                        onSelect={handleSelect}
                                        selected={selected}
                                    >
                                        {currentTabGroup.map((tab, indexGroup) => (
                                            <GMetrixTabsTab key={indexGroup} title={tab.Title}>
                                                <Container fluid className={sectionClassName(tab)}>
                                                    <Row>
                                                        {tab.Components.map((component, componentSectionIndex) => renderComponents(
                                                            component,
                                                            loading,
                                                            componentSectionIndex
                                                        ))}
                                                    </Row>
                                                </Container>
                                            </GMetrixTabsTab>
                                        ))}
                                    </GMetrixTabs>
                                )}
                                <Container
                                    fluid
                                    className={sectionClassName(section)}
                                >
                                    <Row>
                                        {section.Components.map((component, componentSectionIndex) => renderComponents(component, loading, componentSectionIndex))}
                                    </Row>
                                </Container>
                            </React.Fragment>
                        );

                    })
                ) : (

                    //no results found
                    <Container className="report-empty-state mb-2">
                        <div className="report-empty-state__inner">
                            <i aria-hidden="true" className="fa-solid fa-chart-simple report-empty-state__icon"></i>
                            <h5 className="report-empty-state__title">
                                <FormattedMessage defaultMessage="No Activity to Display" id="no_activity_to_display.c8667f46" />
                            </h5>
                            <p className="report-empty-state__reason">
                                <FormattedMessage defaultMessage="No Results Found" id="no_results_found.f1d71249" />
                            </p>
                        </div>
                    </Container>
                )
            ) : (

                //skeleton loading
                reportSections.map((section, reportSectionIndex) => (
                    <Container
                        fluid
                        className="component-wrapper mb-4 border"
                        key={`${reportSectionIndex}-skeleton`}
                    >
                        <Row key={`${reportSectionIndex}-skeleton-row`}>
                            {section.Components.map((component, componentSectionIndex) => renderComponents(component, loading, componentSectionIndex))}
                        </Row>
                    </Container>
                ))
            )}
        </>
    );

}
